import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import { listExtensionTools, uploadSideloadManifest } from '../../api/extensions.api';
import { CLAW_DEFAULT_AI_MODEL, CLAW_DEFAULT_AI_PROVIDER } from '../../constants/clawDefaults.js';
import { OLLAMA_MODEL_OPTIONS } from '../../constants/ollamaModels.js';
import {
  BUILDER_TEMPLATES,
  buildPluginPackageForExport,
  draftFromManifest,
  emptyPluginDraft,
  emptySkillDraft,
  emptyStubCapabilityDraft,
  emptyToolDraft,
  emptyAppPanelDraft,
  APP_PANEL_HTML_MAX,
  manifestFromDraft,
  parsePluginPackageImport,
  slugFromPluginId,
  validatePluginDraft,
} from '../../utils/clawManifestBuilder.js';
import StudioV2ExtensionBuilderAiPanel from './StudioV2ExtensionBuilderAiPanel.jsx';
import { SandboxedPluginFrame, usePluginPayload } from '../Dashboard/components/DashboardPluginPanel.jsx';

const SAMPLE_PANEL_DATA = '[\n  { "title": "First item" },\n  { "title": "Second item" }\n]';

function AppPanelPreview({ panel }) {
  const [sample, setSample] = useState(SAMPLE_PANEL_DATA);
  const [runs, setRuns] = useState(0);
  const data = useMemo(() => {
    try {
      return { value: JSON.parse(sample), error: null };
    } catch {
      return { value: null, error: 'Sample data is not valid JSON' };
    }
  }, [sample]);
  const payload = usePluginPayload(data.value);
  const onRun = useCallback(() => setRuns((n) => n + 1), []);
  return (
    <Stack spacing={1}>
      <TextField
        label="Sample data (preview only)"
        size="small"
        fullWidth
        multiline
        minRows={3}
        value={sample}
        onChange={(e) => setSample(e.target.value)}
        error={Boolean(data.error)}
        helperText={data.error || 'What the panel receives from its flow in an app.'}
        InputProps={{ sx: { fontFamily: 'monospace', fontSize: 12 } }}
      />
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }} data-testid="app-panel-preview">
        <SandboxedPluginFrame name={panel.name} html={panel.html} height={panel.height} payload={payload} onRun={onRun} />
      </Box>
      {runs > 0 && (
        <Typography variant="caption" color="text.secondary">
          {`The panel asked to run its flow ${runs} time${runs === 1 ? '' : 's'}. In an app this runs the panel's flow.`}
        </Typography>
      )}
    </Stack>
  );
}

const PROVIDER_OPTIONS = [
  { value: 'ollama', label: 'Ollama (local)' },
  { value: 'gemini', label: 'Google Gemini' },
  { value: 'openai', label: 'OpenAI' },
];

const MODELS_BY_PROVIDER = {
  ollama: OLLAMA_MODEL_OPTIONS.map((m) => m.value),
  gemini: ['gemini-3.5-flash-lite', 'gemini-2.5-flash', 'gemini-2.5-pro'],
  openai: ['gpt-4o-mini', 'gpt-4o'],
};

function JsonPreview({ manifest }) {
  const text = useMemo(
    () => JSON.stringify(manifest, null, 2),
    [manifest],
  );
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1.5,
        fontSize: 12,
        fontFamily: 'monospace',
        bgcolor: 'action.hover',
        borderRadius: 1,
        overflow: 'auto',
        maxHeight: 320,
      }}
    >
      {text}
    </Box>
  );
}

export default function StudioV2ExtensionBuilder({
  initialManifest = null,
  onUploaded,
  onStatus,
}) {
  const [draft, setDraft] = useState(() => (
    initialManifest ? draftFromManifest(initialManifest) : emptyPluginDraft()
  ));
  const [catalogTools, setCatalogTools] = useState([]);
  const [validationErrors, setValidationErrors] = useState([]);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [expanded, setExpanded] = useState({
    plugin: true, tools: true, skills: true, panels: true, stubs: false, preview: true,
  });
  const packageInputRef = useRef(null);

  useEffect(() => {
    if (initialManifest) setDraft(draftFromManifest(initialManifest));
  }, [initialManifest]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { tools } = await listExtensionTools();
      if (!cancelled) setCatalogTools(tools || []);
    })();
    return () => { cancelled = true; };
  }, []);

  const toolOptions = useMemo(
    () => catalogTools.map((t) => t.id || t.name).filter(Boolean),
    [catalogTools],
  );

  const previewManifest = useMemo(() => manifestFromDraft(draft), [draft]);

  const patchDraft = useCallback((patch) => {
    setDraft((prev) => ({ ...prev, ...patch }));
    setValidationErrors([]);
  }, []);

  const applyTemplate = (key) => {
    const tpl = BUILDER_TEMPLATES[key];
    if (!tpl) return;
    setDraft(tpl.draft());
    setValidationErrors([]);
    onStatus?.('Template loaded — edit fields then validate & sideload.');
  };

  const addTool = () => {
    setDraft((prev) => ({ ...prev, tools: [...(prev.tools || []), emptyToolDraft()] }));
  };

  const updateTool = (index, patch) => {
    setDraft((prev) => {
      const tools = [...(prev.tools || [])];
      tools[index] = { ...tools[index], ...patch };
      return { ...prev, tools };
    });
  };

  const removeTool = (index) => {
    setDraft((prev) => ({
      ...prev,
      tools: (prev.tools || []).filter((_, i) => i !== index),
    }));
  };

  const addSkill = () => {
    setDraft((prev) => ({ ...prev, skills: [...(prev.skills || []), emptySkillDraft()] }));
  };

  const updateSkill = (index, patch) => {
    setDraft((prev) => {
      const skills = [...(prev.skills || [])];
      skills[index] = { ...skills[index], ...patch };
      return { ...prev, skills };
    });
  };

  const removeSkill = (index) => {
    setDraft((prev) => ({
      ...prev,
      skills: (prev.skills || []).filter((_, i) => i !== index),
    }));
  };

  const addAppPanel = () => {
    setDraft((prev) => {
      const n = (prev.appPanels || []).length + 1;
      return {
        ...prev,
        appPanels: [...(prev.appPanels || []), {
          ...emptyAppPanelDraft(),
          id: `${slugFromPluginId(prev.id)}.panel${n > 1 ? `-${n}` : ''}`,
          name: n > 1 ? `Panel ${n}` : 'My panel',
        }],
      };
    });
  };

  const updateAppPanel = (index, patch) => {
    setDraft((prev) => {
      const appPanels = [...(prev.appPanels || [])];
      appPanels[index] = { ...appPanels[index], ...patch };
      return { ...prev, appPanels };
    });
    setValidationErrors([]);
  };

  const removeAppPanel = (index) => {
    setDraft((prev) => ({
      ...prev,
      appPanels: (prev.appPanels || []).filter((_, i) => i !== index),
    }));
  };

  const addStub = () => {
    setDraft((prev) => ({
      ...prev,
      stubCapabilities: [...(prev.stubCapabilities || []), emptyStubCapabilityDraft()],
    }));
  };

  const updateStub = (index, patch) => {
    setDraft((prev) => {
      const stubCapabilities = [...(prev.stubCapabilities || [])];
      stubCapabilities[index] = { ...stubCapabilities[index], ...patch };
      return { ...prev, stubCapabilities };
    });
  };

  const removeStub = (index) => {
    setDraft((prev) => ({
      ...prev,
      stubCapabilities: (prev.stubCapabilities || []).filter((_, i) => i !== index),
    }));
  };

  const onExportPackage = () => {
    const result = validatePluginDraft(draft);
    const manifest = result.manifest;
    const pkg = buildPluginPackageForExport(manifest, {
      author: 'Studio tenant export',
      notes: `Exported ${new Date().toISOString()}`,
    });
    const blob = new Blob([JSON.stringify(pkg, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${manifest.id || 'plugin'}.plugin-package.json`;
    a.click();
    URL.revokeObjectURL(url);
    onStatus?.('Plugin package downloaded.');
  };

  const onImportPackage = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const parsed = parsePluginPackageImport(JSON.parse(await file.text()));
      if (parsed.error || !parsed.manifest) {
        onStatus?.(null);
        setValidationErrors([parsed.error || 'Invalid plugin package']);
        return;
      }
      setDraft(draftFromManifest(parsed.manifest));
      setValidationErrors([]);
      onStatus?.('Imported plugin package into builder.');
    } catch (e) {
      setValidationErrors([e.message || 'Invalid JSON file']);
    }
    event.target.value = '';
  };

  const onValidate = () => {
    const result = validatePluginDraft(draft);
    setValidationErrors(result.errors);
    if (result.valid) {
      onStatus?.('Manifest looks valid — ready to sideload.');
    } else {
      onStatus?.('');
    }
    return result;
  };

  const onSideload = async () => {
    const result = onValidate();
    if (!result.valid) return;
    setUploadBusy(true);
    onStatus?.('');
    const res = await uploadSideloadManifest(result.manifest);
    setUploadBusy(false);
    if (res.error) {
      onStatus?.(null);
      setValidationErrors([res.error]);
      return;
    }
    onStatus?.(`Sideloaded ${result.manifest.id}. Enable it under Plugins and Save.`);
    onUploaded?.(result.manifest);
  };

  const syncCapabilityId = (id) => {
    if (!draft.capabilityId || draft.capabilityId.endsWith('.tools')) {
      patchDraft({ id, capabilityId: `${slugFromPluginId(id)}.tools` });
    } else {
      patchDraft({ id });
    }
  };

  const onApplyAiManifest = useCallback((manifest) => {
    setDraft(draftFromManifest(manifest));
    setValidationErrors([]);
    onStatus?.('AI manifest applied — review and sideload when ready.');
  }, [onStatus]);

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="subtitle2" fontWeight={700}>CLAW plugin builder</Typography>
        <Typography variant="body2" color="text.secondary">
          Compose a tenant plugin manifest: MCP tools (capabilities) and agent skills. Use the AI assistant (Qwen)
          or edit manually, then validate & sideload.
        </Typography>
      </Box>

      <Grid container spacing={2} alignItems="stretch">
        <Grid item xs={12} lg={7}>
          <Stack spacing={2}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {Object.entries(BUILDER_TEMPLATES).map(([key, tpl]) => (
                <Chip
                  key={key}
                  label={tpl.label}
                  onClick={() => applyTemplate(key)}
                  variant="outlined"
                  clickable
                />
              ))}
            </Stack>

            {validationErrors.length > 0 && (
              <Alert severity="error" data-testid="plugin-builder-errors">
                {validationErrors.map((e) => (
                  <Typography key={e} variant="body2" component="div">{e}</Typography>
                ))}
              </Alert>
            )}

            <Accordion
        expanded={expanded.plugin}
        onChange={(_, v) => setExpanded((p) => ({ ...p, plugin: v }))}
        disableGutters
        elevation={0}
        sx={{ border: 1, borderColor: 'divider', borderRadius: 2, '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>Plugin metadata</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={1.5}>
            <TextField
              label="Plugin id"
              size="small"
              fullWidth
              required
              placeholder="com.acme.my-plugin"
              value={draft.id}
              onChange={(e) => syncCapabilityId(e.target.value)}
              helperText="Reverse-DNS id (com.vendor.name)"
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                label="Display name"
                size="small"
                fullWidth
                required
                value={draft.name}
                onChange={(e) => patchDraft({ name: e.target.value })}
              />
              <TextField
                label="Version"
                size="small"
                sx={{ minWidth: 120 }}
                value={draft.version}
                onChange={(e) => patchDraft({ version: e.target.value })}
              />
            </Stack>
            <TextField
              label="Description"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={draft.description}
              onChange={(e) => patchDraft({ description: e.target.value })}
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
              <FormControlLabel
                control={(
                  <Switch
                    checked={draft.defaultEnabled !== false}
                    onChange={(e) => patchDraft({ defaultEnabled: e.target.checked })}
                  />
                )}
                label="Enable by default after sideload"
              />
              <TextField
                select
                label="Requires plan"
                size="small"
                sx={{ minWidth: 180 }}
                value={draft.requiresPlan || ''}
                onChange={(e) => patchDraft({ requiresPlan: e.target.value })}
              >
                <MenuItem value=""><em>Any plan</em></MenuItem>
                <MenuItem value="trial">Trial+</MenuItem>
                <MenuItem value="basic">Basic+</MenuItem>
                <MenuItem value="professional">Professional+</MenuItem>
                <MenuItem value="enterprise">Enterprise</MenuItem>
              </TextField>
            </Stack>
            <TextField
              label="Allowed hosts (for HTTP tools)"
              size="small"
              fullWidth
              value={draft.allowedHosts || ''}
              onChange={(e) => patchDraft({ allowedHosts: e.target.value })}
              placeholder="api.example.com, *.example.org"
              helperText="HTTP tools can only call these hosts"
              inputProps={{ 'data-testid': 'plugin-allowed-hosts' }}
            />
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={expanded.tools}
        onChange={(_, v) => setExpanded((p) => ({ ...p, tools: v }))}
        disableGutters
        elevation={0}
        sx={{ border: 1, borderColor: 'divider', borderRadius: 2, '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography fontWeight={600}>Capabilities · MCP tools</Typography>
            <Chip size="small" label={`${(draft.tools || []).length} tool(s)`} />
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={1.5}>
            <TextField
              label="Capability bundle id"
              size="small"
              fullWidth
              value={draft.capabilityId}
              onChange={(e) => patchDraft({ capabilityId: e.target.value })}
              helperText="Groups tools under one mcp-tool-bundle capability"
            />
            {(draft.tools || []).map((tool, index) => (
              <Card key={`tool-${index}`} variant="outlined">
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Stack spacing={1}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="caption" fontWeight={700}>
                        Tool
                        {' '}
                        {index + 1}
                      </Typography>
                      <IconButton size="small" onClick={() => removeTool(index)} aria-label="Remove tool">
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <TextField
                        label="Tool id"
                        size="small"
                        fullWidth
                        value={tool.id}
                        onChange={(e) => updateTool(index, { id: e.target.value.replace(/\s/g, '_') })}
                        placeholder="my_tool_id"
                      />
                      <TextField
                        label="Tool name"
                        size="small"
                        fullWidth
                        value={tool.name}
                        onChange={(e) => updateTool(index, { name: e.target.value })}
                      />
                    </Stack>
                    <TextField
                      label="Description"
                      size="small"
                      fullWidth
                      value={tool.description}
                      onChange={(e) => updateTool(index, { description: e.target.value })}
                    />
                    <TextField
                      select
                      label="Runs as"
                      size="small"
                      value={tool.source === 'http' ? 'http' : 'extension'}
                      onChange={(e) => updateTool(index, { source: e.target.value })}
                      sx={{ maxWidth: 260 }}
                    >
                      <MenuItem value="extension">Declared only</MenuItem>
                      <MenuItem value="http">HTTP request</MenuItem>
                    </TextField>
                    {tool.source === 'http' && (
                      <TextField
                        label="HTTP request (JSON)"
                        size="small"
                        fullWidth
                        multiline
                        minRows={4}
                        value={tool.httpJson || ''}
                        onChange={(e) => updateTool(index, { httpJson: e.target.value })}
                        placeholder='{"method":"GET","url":"https://api.example.com/search","query":{"q":"{{args.query}}"},"response":{"path":"items","fields":["title","url"],"limit":10}}'
                        helperText="{{args.name}} is replaced with the tool call's argument"
                        InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
                        inputProps={{ 'data-testid': 'plugin-tool-http' }}
                      />
                    )}
                    <TextField
                      label="Input schema (JSON, optional)"
                      size="small"
                      fullWidth
                      multiline
                      minRows={2}
                      value={tool.inputSchemaJson || ''}
                      onChange={(e) => updateTool(index, { inputSchemaJson: e.target.value })}
                      placeholder='{"type":"object","properties":{"query":{"type":"string"}}}'
                    />
                    <TextField
                      label="Output schema (JSON, optional)"
                      size="small"
                      fullWidth
                      multiline
                      minRows={2}
                      value={tool.outputSchemaJson || ''}
                      onChange={(e) => updateTool(index, { outputSchemaJson: e.target.value })}
                    />
                  </Stack>
                </CardContent>
              </Card>
            ))}
            <Button startIcon={<AddIcon />} size="small" onClick={addTool} sx={{ alignSelf: 'flex-start' }}>
              Add tool
            </Button>
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={expanded.skills}
        onChange={(_, v) => setExpanded((p) => ({ ...p, skills: v }))}
        disableGutters
        elevation={0}
        sx={{ border: 1, borderColor: 'divider', borderRadius: 2, '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography fontWeight={600}>Agent skills</Typography>
            <Chip size="small" label={`${(draft.skills || []).length} skill(s)`} />
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={1.5}>
            {(draft.skills || []).map((skill, index) => (
              <Card key={`skill-${index}`} variant="outlined">
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Stack spacing={1.25}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                      <Typography variant="caption" fontWeight={700}>
                        Skill
                        {' '}
                        {index + 1}
                      </Typography>
                      <IconButton size="small" onClick={() => removeSkill(index)} aria-label="Remove skill">
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <TextField
                        label="Skill id"
                        size="small"
                        fullWidth
                        value={skill.id}
                        onChange={(e) => updateSkill(index, { id: e.target.value.replace(/\s/g, '-') })}
                      />
                      <TextField
                        label="Skill name"
                        size="small"
                        fullWidth
                        value={skill.name}
                        onChange={(e) => updateSkill(index, { name: e.target.value })}
                      />
                    </Stack>
                    <TextField
                      label="Description"
                      size="small"
                      fullWidth
                      value={skill.description}
                      onChange={(e) => updateSkill(index, { description: e.target.value })}
                    />
                    <TextField
                      label="System prompt"
                      size="small"
                      fullWidth
                      multiline
                      minRows={3}
                      value={skill.systemPrompt}
                      onChange={(e) => updateSkill(index, { systemPrompt: e.target.value })}
                      helperText="Required for sideload — inline prompt only (not systemPromptPath)"
                    />
                    <Autocomplete
                      multiple
                      size="small"
                      options={toolOptions}
                      value={skill.allowedTools || []}
                      onChange={(_, value) => updateSkill(index, { allowedTools: value })}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Allowed tools"
                          helperText="Tool ids this skill may call at runtime (from catalog + tools above)"
                        />
                      )}
                    />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <TextField
                        select
                        label="Provider"
                        size="small"
                        fullWidth
                        value={skill.defaultProvider || CLAW_DEFAULT_AI_PROVIDER}
                        onChange={(e) => {
                          const provider = e.target.value;
                          const models = MODELS_BY_PROVIDER[provider] || [];
                          updateSkill(index, {
                            defaultProvider: provider,
                            defaultModel: models[0] || CLAW_DEFAULT_AI_MODEL,
                          });
                        }}
                      >
                        {PROVIDER_OPTIONS.map((p) => (
                          <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                        ))}
                      </TextField>
                      <TextField
                        select
                        label="Default model"
                        size="small"
                        fullWidth
                        value={skill.defaultModel || CLAW_DEFAULT_AI_MODEL}
                        onChange={(e) => updateSkill(index, { defaultModel: e.target.value })}
                      >
                        {(MODELS_BY_PROVIDER[skill.defaultProvider] || MODELS_BY_PROVIDER.ollama).map((m) => (
                          <MenuItem key={m} value={m}>{m}</MenuItem>
                        ))}
                      </TextField>
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <TextField
                        label="Temperature"
                        size="small"
                        type="number"
                        inputProps={{ min: 0, max: 2, step: 0.05 }}
                        fullWidth
                        value={skill.temperature ?? 0.25}
                        onChange={(e) => updateSkill(index, { temperature: parseFloat(e.target.value) })}
                      />
                      <TextField
                        label="Max tokens"
                        size="small"
                        type="number"
                        inputProps={{ min: 256, step: 256 }}
                        fullWidth
                        value={skill.maxTokens ?? 2048}
                        onChange={(e) => updateSkill(index, { maxTokens: parseInt(e.target.value, 10) })}
                      />
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            ))}
            <Button startIcon={<AddIcon />} size="small" onClick={addSkill} sx={{ alignSelf: 'flex-start' }}>
              Add skill
            </Button>
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={expanded.panels}
        onChange={(_, v) => setExpanded((p) => ({ ...p, panels: v }))}
        disableGutters
        elevation={0}
        sx={{ border: 1, borderColor: 'divider', borderRadius: 2, '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography fontWeight={600}>App panels</Typography>
            <Chip size="small" label={`${(draft.appPanels || []).length} panel(s)`} />
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Panels people can add to apps. Write plain HTML and script; it runs in a locked-down frame with no network access.
            Use <code>interviewflow.onData(fn)</code> to get the panel&apos;s data, <code>interviewflow.run(input)</code> to run its flow,
            and <code>interviewflow.resize(px)</code> to change its height.
          </Typography>
          <Stack spacing={1.5}>
            {(draft.appPanels || []).map((panel, index) => (
              <Card key={`panel-${index}`} variant="outlined" data-testid="plugin-app-panel">
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" fontWeight={700}>{panel.name || `Panel ${index + 1}`}</Typography>
                      <IconButton size="small" onClick={() => removeAppPanel(index)} aria-label="Remove app panel">
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <TextField label="Name" size="small" fullWidth value={panel.name} onChange={(e) => updateAppPanel(index, { name: e.target.value })} />
                      <TextField label="Panel id" size="small" fullWidth value={panel.id} onChange={(e) => updateAppPanel(index, { id: e.target.value })} />
                    </Stack>
                    <TextField label="Description" size="small" fullWidth value={panel.description} onChange={(e) => updateAppPanel(index, { description: e.target.value })} />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <TextField
                        label="Data field"
                        size="small"
                        fullWidth
                        value={panel.outputKey}
                        onChange={(e) => updateAppPanel(index, { outputKey: e.target.value })}
                        helperText="The flow result field this panel shows"
                      />
                      <TextField
                        label="Height (px)"
                        size="small"
                        type="number"
                        fullWidth
                        inputProps={{ min: 120, max: 1600, step: 20 }}
                        value={panel.height}
                        onChange={(e) => updateAppPanel(index, { height: parseInt(e.target.value, 10) || '' })}
                      />
                    </Stack>
                    <TextField
                      label="Panel HTML"
                      size="small"
                      fullWidth
                      multiline
                      minRows={8}
                      maxRows={24}
                      value={panel.html}
                      onChange={(e) => updateAppPanel(index, { html: e.target.value })}
                      helperText={`${(panel.html || '').length.toLocaleString()} / ${APP_PANEL_HTML_MAX.toLocaleString()} characters`}
                      error={(panel.html || '').length > APP_PANEL_HTML_MAX}
                      InputProps={{ sx: { fontFamily: 'monospace', fontSize: 12 } }}
                      inputProps={{ 'data-testid': 'plugin-app-panel-html' }}
                    />
                    <AppPanelPreview panel={panel} />
                  </Stack>
                </CardContent>
              </Card>
            ))}
            <Button startIcon={<AddIcon />} size="small" onClick={addAppPanel} sx={{ alignSelf: 'flex-start' }} data-testid="plugin-add-app-panel">
              Add app panel
            </Button>
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={expanded.stubs}
        onChange={(_, v) => setExpanded((p) => ({ ...p, stubs: v }))}
        disableGutters
        elevation={0}
        sx={{ border: 1, borderColor: 'divider', borderRadius: 2, '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography fontWeight={600}>Future capabilities (stubs)</Typography>
            <Chip size="small" label={`${(draft.stubCapabilities || []).length} stub(s)`} />
          </Stack>
        </AccordionSummary>
        <AccordionDetails>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            Declare flow-node or connector placeholders for repo deploy — not active until shipped in a platform bundle.
          </Typography>
          <Stack spacing={1.5}>
            {(draft.stubCapabilities || []).map((stub, index) => (
              <Card key={`stub-${index}`} variant="outlined">
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" fontWeight={700}>Stub {index + 1}</Typography>
                      <IconButton size="small" onClick={() => removeStub(index)} aria-label="Remove stub">
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <TextField select label="Kind" size="small" fullWidth value={stub.kind} onChange={(e) => updateStub(index, { kind: e.target.value })}>
                        <MenuItem value="flow-node">flow-node</MenuItem>
                        <MenuItem value="connector">connector</MenuItem>
                      </TextField>
                      <TextField label="Capability id" size="small" fullWidth value={stub.id} onChange={(e) => updateStub(index, { id: e.target.value })} />
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <TextField label="Type / connector id" size="small" fullWidth value={stub.type} onChange={(e) => updateStub(index, { type: e.target.value })} />
                      <TextField label="Name" size="small" fullWidth value={stub.name} onChange={(e) => updateStub(index, { name: e.target.value })} />
                    </Stack>
                    <TextField label="Description" size="small" fullWidth value={stub.description} onChange={(e) => updateStub(index, { description: e.target.value })} />
                  </Stack>
                </CardContent>
              </Card>
            ))}
            <Button startIcon={<AddIcon />} size="small" onClick={addStub} sx={{ alignSelf: 'flex-start' }}>
              Add capability stub
            </Button>
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={expanded.preview}
        onChange={(_, v) => setExpanded((p) => ({ ...p, preview: v }))}
        disableGutters
        elevation={0}
        sx={{ border: 1, borderColor: 'divider', borderRadius: 2, '&:before': { display: 'none' } }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography fontWeight={600}>Manifest preview</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <JsonPreview manifest={previewManifest} />
        </AccordionDetails>
      </Accordion>

            <Divider />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
              <Button variant="outlined" startIcon={<DownloadIcon />} onClick={onExportPackage}>
                Export package
              </Button>
              <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={() => packageInputRef.current?.click()}>
                Import package
              </Button>
              <input ref={packageInputRef} type="file" accept="application/json,.json" hidden onChange={onImportPackage} />
              <Button variant="outlined" onClick={onValidate} disabled={uploadBusy}>
                Validate
              </Button>
              <Button
                variant="contained"
                startIcon={<UploadFileIcon />}
                onClick={onSideload}
                disabled={uploadBusy}
                data-testid="plugin-sideload"
              >
                {uploadBusy ? 'Sideloading…' : 'Validate & sideload'}
              </Button>
            </Stack>
          </Stack>
        </Grid>
        <Grid item xs={12} lg={5}>
          <StudioV2ExtensionBuilderAiPanel
            manifest={previewManifest}
            onApplyManifest={onApplyAiManifest}
          />
        </Grid>
      </Grid>
    </Stack>
  );
}
